import type { DateTime } from 'luxon';
import { isMidnight, isMultiDay } from './dates';

// Standard date labels. Format a date for display through these rather than a
// Luxon format string written at the call site, so the same date reads the
// same way on every surface; when a surface needs a label this doesn't offer,
// add it here. Times belong to formatTime.

type DateOptions = {
  /** Name the year, for a list that can run past the current one. */
  includeYear?: boolean;
};

type DateSpan = {
  startDate: DateTime;
  endDate: DateTime;
};

const DATE_FORMAT = 'ccc, MMM d';
const DATE_FORMAT_WITH_YEAR = 'ccc, MMM d, yyyy';

// Joins a range's two ends, as formatTime's time ranges do ("9:00 am – 3:00 pm").
const EN_DASH = '–';

/** "Wed, Sep 30", or "Wed, Sep 30, 2026" with the year. */
export const formatDate = (
  date: DateTime,
  { includeYear = false }: DateOptions = {}
): string => date.toFormat(includeYear ? DATE_FORMAT_WITH_YEAR : DATE_FORMAT);

/**
 * The days a span covers, split where the label may wrap: one part for a span
 * within a single day, or its first and last days with the dash kept on the
 * first (`['Wed, Sep 30 –', 'Fri, Oct 2']`). With the year, a year both ends
 * share is named once, at the end; a span crossing New Year names both.
 *
 * The last day is the one the span actually reaches. An end at midnight is
 * exclusive, so it closes the day before: an all-day end always is one (the
 * midnight after the last day), and a timed span ending at 12:00 AM stops short
 * of the next day. It is the rule isMultiDay applies, so the two agree on
 * whether there is a second day to name.
 */
export const formatDateRangeParts = (
  { startDate, endDate }: DateSpan,
  { includeYear = false }: DateOptions = {}
): [string] | [string, string] => {
  if (!isMultiDay(startDate, endDate)) {
    return [formatDate(startDate, { includeYear })];
  }

  const lastDay = isMidnight(endDate) ? endDate.minus({ days: 1 }) : endDate;
  const start = formatDate(startDate, {
    includeYear: includeYear && !startDate.hasSame(lastDay, 'year'),
  });

  return [`${start} ${EN_DASH}`, formatDate(lastDay, { includeYear })];
};

/**
 * The days a span covers as one label: "Wed, Sep 30" within a single day,
 * "Wed, Sep 30 – Fri, Oct 2" across several. See formatDateRangeParts.
 */
export const formatDateRange = (
  span: DateSpan,
  options?: DateOptions
): string => formatDateRangeParts(span, options).join(' ');
